import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import type { ClientLite, Invoice } from './types.ts';

import { ApiError, apiFetch } from '@/lib/api';
import { formatBRL } from '@/lib/utils';


interface FormState {
  issuerCompanyId: string;
  clientId: string;
  certificateId: string;
  competencia: string;
  serie: string;
  tpEmit: number;
  tpAmb: number;
  cLocPrestacao: string;
  cPaisPrestacao: string;
  opConsumServ: string;
  servicoDescricao: string;
  servicoCodigo: string;
  cTribMun: string;
  cNbs: string;
  cIntContrib: string;
  valorServico: number;
  aliquotaIss: number;
  vDescIncond: number;
  vDescCond: number;
  vReceb: number;
  tribIssqn: number;
  tpRetIssqn: number;
  tpImunidade: string;
  substChSubstda: string;
  substCMotivo: string;
  substXMotivo: string;
}

const EMPTY: FormState = {
  issuerCompanyId: '',
  clientId: '',
  certificateId: '',
  competencia: new Date().toISOString().slice(0, 7),
  serie: 'NFSE',
  tpEmit: 1,
  tpAmb: 2,
  cLocPrestacao: '3524402',
  cPaisPrestacao: '1058',
  opConsumServ: '',
  servicoDescricao: '',
  servicoCodigo: '010801',
  cTribMun: '',
  cNbs: '',
  cIntContrib: '',
  valorServico: 0,
  aliquotaIss: 5,
  vDescIncond: 0,
  vDescCond: 0,
  vReceb: 0,
  tribIssqn: 1,
  tpRetIssqn: 1,
  tpImunidade: '',
  substChSubstda: '',
  substCMotivo: '',
  substXMotivo: '',
};

function fromInvoice(inv: Invoice & { issuerCompanyId?: string | null }): FormState {
  return {
    issuerCompanyId: inv.issuerCompanyId ?? '',
    clientId: inv.clientId,
    certificateId: inv.certificateId ?? '',
    competencia: inv.competencia.slice(0, 7),
    serie: inv.serie,
    tpEmit: inv.tpEmit,
    tpAmb: inv.tpAmb,
    cLocPrestacao: inv.cLocPrestacao,
    cPaisPrestacao: inv.cPaisPrestacao,
    opConsumServ: inv.opConsumServ?.toString() ?? '',
    servicoDescricao: inv.servicoDescricao,
    servicoCodigo: inv.servicoCodigo,
    cTribMun: inv.cTribMun ?? '',
    cNbs: inv.cNbs ?? '',
    cIntContrib: inv.cIntContrib ?? '',
    valorServico: Number(inv.valorServico),
    aliquotaIss: Number(inv.aliquotaIss),
    vDescIncond: Number(inv.vDescIncond ?? 0),
    vDescCond: Number(inv.vDescCond ?? 0),
    vReceb: Number(inv.vReceb ?? 0),
    tribIssqn: inv.tribIssqn,
    tpRetIssqn: inv.tpRetIssqn,
    tpImunidade: inv.tpImunidade?.toString() ?? '',
    substChSubstda: inv.substChSubstda ?? '',
    substCMotivo: inv.substCMotivo ?? '',
    substXMotivo: inv.substXMotivo ?? '',
  };
}

export function InvoiceForm({
  invoice,
  onClose,
  onSaved,
}: {
  invoice: Invoice | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(invoice ? fromInvoice(invoice) : EMPTY);
  const [error, setError] = useState<string | null>(null);

  // Carrega clientes para o select
  const clients = useQuery({
    queryKey: ['clients', 'select'],
    queryFn: () =>
      apiFetch<{ rows: ClientLite[] }>('/clients?pageSize=200').then((r) => r.rows),
  });

  // Carrega empresas emissoras (multi-CNPJ)
  const issuerCompanies = useQuery({
    queryKey: ['issuer-companies', 'select'],
    queryFn: () =>
      apiFetch<Array<{ id: string; razaoSocial: string; cnpj: string; isDefault: boolean }>>(
        '/issuer-companies',
      ),
  });

  useEffect(() => {
    setForm(invoice ? fromInvoice(invoice) : EMPTY);
  }, [invoice]);

  // Cálculo em tempo real (espelha invoice-calculator do backend)
  const calc = useMemo(() => {
    const base = Math.max(0, form.valorServico - form.vDescIncond);
    const iss = Math.round(base * (form.aliquotaIss / 100) * 100) / 100;
    const liquido = Math.round((form.valorServico - iss - form.vDescIncond) * 100) / 100;
    return { iss, liquido };
  }, [form.valorServico, form.aliquotaIss, form.vDescIncond]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form);
      if (invoice) {
        return apiFetch<Invoice>(`/invoices/${invoice.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      }
      return apiFetch<Invoice>('/invoices', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => onSaved(),
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.body);
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao salvar');
      }
    },
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!form.clientId) {
      setError('Selecione um cliente');
      return;
    }
    if (form.valorServico <= 0) {
      setError('Valor do serviço deve ser maior que zero');
      return;
    }
    save.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{invoice ? 'Editar nota' : 'Nova nota fiscal'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Accordion title="1. Identificação da DPS" defaultOpen>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Empresa emissora">
                <select
                  className="input"
                  value={form.issuerCompanyId}
                  onChange={(e) => setForm((s) => ({ ...s, issuerCompanyId: e.target.value }))}
                >
                  <option value="">(usar padrão)</option>
                  {issuerCompanies.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.razaoSocial}
                      {c.isDefault ? ' ★' : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Cliente *">
                <select
                  required
                  className="input"
                  value={form.clientId}
                  onChange={(e) => setForm((s) => ({ ...s, clientId: e.target.value }))}
                >
                  <option value="">— escolha —</option>
                  {clients.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.razaoSocial}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Competência (YYYY-MM) *">
                <input
                  required
                  type="month"
                  className="input"
                  value={form.competencia}
                  onChange={(e) => setForm((s) => ({ ...s, competencia: e.target.value }))}
                />
              </Field>
              <Field label="Ambiente">
                <select
                  className="input"
                  value={form.tpAmb}
                  onChange={(e) => setForm((s) => ({ ...s, tpAmb: Number(e.target.value) }))}
                >
                  <option value={2}>2 — Homologação</option>
                  <option value={1}>1 — Produção</option>
                </select>
              </Field>
              <Field label="Série">
                <input
                  className="input"
                  maxLength={5}
                  value={form.serie}
                  onChange={(e) => setForm((s) => ({ ...s, serie: e.target.value }))}
                />
              </Field>
              <Field label="tpEmit">
                <select
                  className="input"
                  value={form.tpEmit}
                  onChange={(e) => setForm((s) => ({ ...s, tpEmit: Number(e.target.value) }))}
                >
                  <option value={1}>1 — Prestador</option>
                  <option value={2}>2 — Tomador</option>
                  <option value={3}>3 — Intermediário</option>
                </select>
              </Field>
            </div>
          </Accordion>

          <Accordion title="2. Local de prestação">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field label="IBGE prestação (7 dígitos)">
                <input
                  className="input font-mono"
                  value={form.cLocPrestacao}
                  onChange={(e) => setForm((s) => ({ ...s, cLocPrestacao: e.target.value }))}
                />
              </Field>
              <Field label="País prestação (4 dígitos)">
                <input
                  className="input font-mono"
                  value={form.cPaisPrestacao}
                  onChange={(e) => setForm((s) => ({ ...s, cPaisPrestacao: e.target.value }))}
                />
              </Field>
              <Field label="opConsumServ">
                <select
                  className="input"
                  value={form.opConsumServ}
                  onChange={(e) => setForm((s) => ({ ...s, opConsumServ: e.target.value }))}
                >
                  <option value="">—</option>
                  <option value="0">0</option>
                  <option value="1">1</option>
                </select>
              </Field>
            </div>
          </Accordion>

          <Accordion title="3. Serviço">
            <div className="space-y-3">
              <Field label="Descrição do serviço (1-2000 chars) *">
                <textarea
                  required
                  rows={3}
                  maxLength={2000}
                  className="input"
                  value={form.servicoDescricao}
                  onChange={(e) => setForm((s) => ({ ...s, servicoDescricao: e.target.value }))}
                />
              </Field>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Field label="cTribNac (6 dígitos) *">
                  <input
                    required
                    className="input font-mono"
                    maxLength={6}
                    value={form.servicoCodigo}
                    onChange={(e) => setForm((s) => ({ ...s, servicoCodigo: e.target.value }))}
                  />
                </Field>
                <Field label="cTribMun">
                  <input
                    className="input"
                    value={form.cTribMun}
                    onChange={(e) => setForm((s) => ({ ...s, cTribMun: e.target.value }))}
                  />
                </Field>
                <Field label="cNBS">
                  <input
                    className="input"
                    value={form.cNbs}
                    onChange={(e) => setForm((s) => ({ ...s, cNbs: e.target.value }))}
                  />
                </Field>
                <Field label="cIntContrib">
                  <input
                    className="input"
                    value={form.cIntContrib}
                    onChange={(e) => setForm((s) => ({ ...s, cIntContrib: e.target.value }))}
                  />
                </Field>
              </div>
            </div>
          </Accordion>

          <Accordion title="4. Valores e tributação" defaultOpen>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <Field label="Valor do serviço *">
                <input
                  required
                  type="number"
                  step="0.01"
                  min={0.01}
                  className="input"
                  value={form.valorServico || ''}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, valorServico: Number(e.target.value) || 0 }))
                  }
                />
              </Field>
              <Field label="Alíquota ISS (%) *">
                <input
                  required
                  type="number"
                  step="0.0001"
                  min={0}
                  max={100}
                  className="input"
                  value={form.aliquotaIss}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, aliquotaIss: Number(e.target.value) || 0 }))
                  }
                />
              </Field>
              <Field label="Desconto incondicional">
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  className="input"
                  value={form.vDescIncond || ''}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, vDescIncond: Number(e.target.value) || 0 }))
                  }
                />
              </Field>
              <Field label="Desconto condicional">
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  className="input"
                  value={form.vDescCond || ''}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, vDescCond: Number(e.target.value) || 0 }))
                  }
                />
              </Field>
              <Field label="Valor recebido">
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  className="input"
                  value={form.vReceb || ''}
                  onChange={(e) => setForm((s) => ({ ...s, vReceb: Number(e.target.value) || 0 }))}
                />
              </Field>
              <Field label="tribISSQN">
                <select
                  className="input"
                  value={form.tribIssqn}
                  onChange={(e) => setForm((s) => ({ ...s, tribIssqn: Number(e.target.value) }))}
                >
                  <option value={1}>1 — Tributável</option>
                  <option value={2}>2 — Exportação</option>
                  <option value={3}>3 — Não Incidência</option>
                  <option value={4}>4 — Imunidade</option>
                </select>
              </Field>
              <Field label="tpRetISSQN">
                <select
                  className="input"
                  value={form.tpRetIssqn}
                  onChange={(e) => setForm((s) => ({ ...s, tpRetIssqn: Number(e.target.value) }))}
                >
                  <option value={1}>1 — Não Retido</option>
                  <option value={2}>2 — Tomador</option>
                  <option value={3}>3 — Intermediário</option>
                </select>
              </Field>
              {form.tribIssqn === 4 && (
                <Field label="tpImunidade *">
                  <input
                    required
                    type="number"
                    className="input"
                    value={form.tpImunidade}
                    onChange={(e) => setForm((s) => ({ ...s, tpImunidade: e.target.value }))}
                  />
                </Field>
              )}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3 rounded-md border border-border bg-muted/30 p-3 text-sm">
              <div className="space-y-0.5">
                <div className="text-xs text-muted-foreground">Valor do ISS</div>
                <div className="font-medium">{formatBRL(calc.iss)}</div>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs text-muted-foreground">Valor líquido</div>
                <div className="font-medium">{formatBRL(calc.liquido)}</div>
              </div>
            </div>
          </Accordion>

          <Accordion title="5. Substituição (opcional)">
            <div className="space-y-3">
              <Field label="Chave NFSe substituída (50 chars)">
                <input
                  maxLength={50}
                  className="input font-mono"
                  value={form.substChSubstda}
                  onChange={(e) => setForm((s) => ({ ...s, substChSubstda: e.target.value }))}
                />
              </Field>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Código do motivo">
                  <input
                    className="input"
                    value={form.substCMotivo}
                    onChange={(e) => setForm((s) => ({ ...s, substCMotivo: e.target.value }))}
                  />
                </Field>
                <Field label="Descrição do motivo">
                  <input
                    className="input"
                    value={form.substXMotivo}
                    onChange={(e) => setForm((s) => ({ ...s, substXMotivo: e.target.value }))}
                  />
                </Field>
              </div>
            </div>
          </Accordion>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {save.isPending ? 'Salvando…' : invoice ? 'Salvar alterações' : 'Criar rascunho'}
            </button>
          </div>
        </form>

        <style>{`
          .input {
            width: 100%;
            border: 1px solid hsl(var(--input));
            background: hsl(var(--background));
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 0.875rem;
          }
          .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
        `}</style>
      </div>
    </div>
  );
}

function toPayload(s: FormState) {
  const payload: Record<string, unknown> = {
    clientId: s.clientId,
    ...(s.issuerCompanyId ? { issuerCompanyId: s.issuerCompanyId } : {}),
    competencia: s.competencia,
    serie: s.serie,
    tpEmit: s.tpEmit,
    tpAmb: s.tpAmb,
    cLocPrestacao: s.cLocPrestacao,
    cPaisPrestacao: s.cPaisPrestacao,
    servicoDescricao: s.servicoDescricao,
    servicoCodigo: s.servicoCodigo,
    valorServico: s.valorServico,
    aliquotaIss: s.aliquotaIss,
    tribIssqn: s.tribIssqn,
    tpRetIssqn: s.tpRetIssqn,
  };
  if (s.certificateId) payload.certificateId = s.certificateId;
  if (s.opConsumServ) payload.opConsumServ = Number(s.opConsumServ);
  if (s.cTribMun) payload.cTribMun = s.cTribMun;
  if (s.cNbs) payload.cNbs = s.cNbs;
  if (s.cIntContrib) payload.cIntContrib = s.cIntContrib;
  if (s.vDescIncond > 0) payload.vDescIncond = s.vDescIncond;
  if (s.vDescCond > 0) payload.vDescCond = s.vDescCond;
  if (s.vReceb > 0) payload.vReceb = s.vReceb;
  if (s.tpImunidade) payload.tpImunidade = Number(s.tpImunidade);
  if (s.substChSubstda) payload.substChSubstda = s.substChSubstda;
  if (s.substCMotivo) payload.substCMotivo = s.substCMotivo;
  if (s.substXMotivo) payload.substXMotivo = s.substXMotivo;
  return payload;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function Accordion({
  title,
  children,
  defaultOpen,
}: {
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <fieldset className="rounded-md border border-border bg-card">
      <legend
        role="button"
        tabIndex={0}
        onClick={() => setOpen((s) => !s)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((s) => !s);
          }
        }}
        className="ml-3 cursor-pointer rounded-md bg-background px-2 py-0.5 text-sm font-medium select-none flex items-center gap-2"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {title}
      </legend>
      {open && <div className="p-3">{children}</div>}
    </fieldset>
  );
}
